/**
 * Selekt component - the select replacement itself.
 *
 * The original <select> stays in the DOM as the VALUE HOLDER: at any moment it
 * contains only the selected option(s) (rebuilt on every change, then a native
 * 'change' event is dispatched), so forms submit normally and any listener /
 * future validation library keeps working. The full dataset lives in the data
 * layer (data.js), never as <option> nodes.
 *
 * Modes: 'simple' (dropdown; single shows a search over the threshold,
 * multiple is a select2-like tags field), 'complex' (bigger panel: search,
 * results, extra.description pane, Add button), 'switch' (inline segmented
 * control, flat options, single only).
 *
 * Full reference: docs/libs/selekt.md.
 */
import { createSource, flatten, normalizeItems, selectableDescendants } from './data.js';

// Interface strings go through g.translate (identity until the language files
// land); API data is never translated.
const t = (s) => (window.g && window.g.translate ? window.g.translate(s) : s);

let uidCounter = 0;

export class Selekt {
	constructor(el, opts) {
		this.el = el;
		this.opts = opts;
		this.uid = `selekt-${++uidCounter}`;
		this.multiple = opts.multiple !== null && opts.multiple !== undefined ? !!opts.multiple : el.multiple;
		if (this.multiple && !el.multiple) el.multiple = true;

		// selection: value(string) -> { item, via } (via = parent value when
		// auto-selected by selectChildren; via entries are hidden when closed
		// and locked in the list).
		this.selection = new Map();
		this.search = '';
		this.page = 1;
		this.more = false;
		this.seq = 0; // request token - stale async responses are dropped
		this.loadedFlat = []; // [{ item, depth }] currently rendered
		this.activeIndex = -1;
		this.open = false;

		const domItems = this.readDomItems();
		this.source = createSource({
			items: opts.items || domItems,
			endpoint: opts.endpoint || '',
			perPage: opts.perPage,
			getParams: () => this.collectParams(),
		});

		// Initial selection: trust the holder's selected options (value+label).
		// Empty values are placeholders (browsers auto-select the first option
		// of a single select), not real selections.
		Array.from(el.selectedOptions || []).forEach((o) => {
			if (o.value === '') return;
			this.selection.set(String(o.value), { item: { value: o.value, label: o.text, extra: {}, children: null }, via: null });
		});
		this.initialValues = this.values();

		this.build();
		this.bindDepends();
		this.bindFormReset();
		this.syncHolder(false);
		this.lastFired = this.values().join('');
		el.selekt = this;
	}

	// ---- setup ---------------------------------------------------------------

	// Read the original <option>/<optgroup> tree as the local dataset.
	readDomItems() {
		const parse = (nodes) => Array.from(nodes).map((node) => {
			if (node.tagName === 'OPTGROUP') {
				return { value: `group-${++uidCounter}`, label: node.label, disabled: true, children: parse(node.children) };
			}
			return { value: node.value, label: node.text, disabled: node.disabled };
		});
		const items = parse(this.el.children).filter((it) => it.label !== '' || it.children);
		return items.length ? items : null;
	}

	collectParams() {
		const params = { ...this.opts.params };
		const dep = this.opts.dependsOn;
		if (dep && dep.source && dep.param) {
			const parent = document.querySelector(dep.source);
			if (parent && parent.value) params[dep.param] = coerce(parent.value);
		}
		return params;
	}

	build() {
		const el = this.el;
		this.wrap = document.createElement('div');
		this.wrap.className = `selekt selekt-${this.multiple ? 'multiple' : 'single'} selekt-mode-${this.opts.mode}`;
		if (this.multiple) this.wrap.classList.add(`selekt-align-${this.opts.controlsAlign === 'bottom' ? 'bottom' : 'top'}`);
		el.parentNode.insertBefore(this.wrap, el);
		this.wrap.appendChild(el);
		el.classList.add('selekt-holder');
		el.tabIndex = -1;
		el.setAttribute('aria-hidden', 'true');

		if (this.opts.mode === 'switch') return this.buildSwitch();
		this.buildControl();
		if (this.opts.mode === 'modal') this.buildModal();
		else this.buildPanel();
	}

	// Closed control: a .form-select so Bootstrap sizing/validation/input-group
	// styling applies untouched. Size + validation classes are copied from the
	// original select.
	buildControl() {
		const toggle = document.createElement('button');
		toggle.type = 'button';
		toggle.className = 'selekt-toggle form-select';
		['form-select-sm', 'form-select-lg', 'is-invalid', 'is-valid'].forEach((c) => {
			if (this.el.classList.contains(c)) toggle.classList.add(c);
		});
		toggle.setAttribute('role', 'combobox');
		toggle.setAttribute('aria-expanded', 'false');
		toggle.setAttribute('aria-haspopup', 'listbox');
		toggle.setAttribute('aria-controls', `${this.uid}-list`);
		if (this.el.disabled) toggle.disabled = true;
		this.toggle = toggle;

		if (this.multiple) {
			this.tagsEl = document.createElement('span');
			this.tagsEl.className = 'selekt-tags';
			this.inline = document.createElement('input');
			this.inline.type = 'text';
			this.inline.size = 1; // otherwise the browser's default size forces wraps
			this.inline.className = 'selekt-inline-input';
			this.inline.setAttribute('autocomplete', 'off');
			this.inline.setAttribute('aria-label', t('Search options'));
			if (this.opts.mode === 'modal') this.inline.readOnly = true; // search lives in the modal
			// The input lives INSIDE the tags container so it flows inline right
			// after the last tag - never onto an (empty-looking) row of its own.
			this.tagsEl.appendChild(this.inline);
			toggle.append(this.tagsEl);
			this.inline.addEventListener('input', () => { this.openPanel(); this.onSearch(this.inline.value); });
			this.inline.addEventListener('keydown', (e) => this.onKeydown(e));
			this.inline.addEventListener('focus', () => this.openPanel());
		} else {
			this.valueEl = document.createElement('span');
			this.valueEl.className = 'selekt-value';
			toggle.appendChild(this.valueEl);
		}

		this.clearBtn = document.createElement('span');
		this.clearBtn.className = 'selekt-clear';
		this.clearBtn.setAttribute('role', 'button');
		this.clearBtn.setAttribute('aria-label', t('Clear selection'));
		this.clearBtn.innerHTML = '<i class="ri-close-line" aria-hidden="true"></i>';
		this.clearBtn.addEventListener('click', (e) => { e.stopPropagation(); this.clear(); });
		toggle.appendChild(this.clearBtn);

		toggle.addEventListener('click', () => {
			if (this.multiple) { this.openPanel(); this.inline.focus(); return; }
			this.open ? this.closePanel() : this.openPanel();
		});
		toggle.addEventListener('keydown', (e) => { if (!this.multiple) this.onKeydown(e); });

		this.wrap.appendChild(toggle);
		this.renderClosed();
	}

	buildPanel() {
		const p = document.createElement('div');
		p.className = 'selekt-panel';
		p.hidden = true;

		// Panel search: single mode only (multiple searches through the inline
		// input in the control). Simple shows it over the threshold or on ajax;
		// complex always shows it.
		this.panelSearch = null;
		if (!this.multiple) {
			const box = document.createElement('div');
			box.className = 'selekt-panel-search';
			box.innerHTML = '<i class="ri-search-line" aria-hidden="true"></i>';
			this.panelSearch = document.createElement('input');
			this.panelSearch.type = 'text';
			this.panelSearch.className = 'selekt-search-input';
			this.panelSearch.placeholder = t('Search...');
			this.panelSearch.setAttribute('autocomplete', 'off');
			this.panelSearch.setAttribute('aria-label', t('Search options'));
			box.appendChild(this.panelSearch);
			p.appendChild(box);
			this.panelSearch.addEventListener('input', () => this.onSearch(this.panelSearch.value));
			this.panelSearch.addEventListener('keydown', (e) => this.onKeydown(e));
			this.searchBox = box;
		}

		p.appendChild(this.buildListBody());
		this.wrap.appendChild(p);
		this.panel = p;

		this.onDocClick = (e) => { if (!this.wrap.contains(e.target)) this.closePanel(); };
	}

	// The results area - shared by the dropdown panel and the modal mode.
	// complex/modal get the two-column layout (list left, side pane right with
	// the Add action + the active item's details), like the workspace modal.
	buildListBody() {
		const body = document.createElement('div');
		body.className = 'selekt-panel-body';
		this.listEl = document.createElement('div');
		this.listEl.className = 'selekt-list';
		this.listEl.id = `${this.uid}-list`;
		this.listEl.setAttribute('role', 'listbox');
		if (this.multiple) this.listEl.setAttribute('aria-multiselectable', 'true');
		body.appendChild(this.listEl);

		this.sideEl = null;
		if (this.opts.mode === 'complex' || this.opts.mode === 'modal') {
			const side = document.createElement('div');
			side.className = 'selekt-side';
			side.innerHTML = `<div class="selekt-side-create" hidden><div class="selekt-side-label"><i class="ri-add-line" aria-hidden="true"></i> ${t('New item')}</div><button type="button" class="btn btn-sm btn-primary selekt-side-add"></button><div class="selekt-side-hint selekt-side-create-hint">${t('Type in the search to add a new option.')}</div></div><div class="selekt-side-info"><div class="selekt-side-label"><i class="ri-information-line" aria-hidden="true"></i> ${t('Details')}</div><div class="selekt-side-title"></div><div class="selekt-side-text"></div><div class="selekt-side-hint selekt-side-info-hint">${t('Hover an option to see its details.')}</div></div>`;
			body.appendChild(side);
			this.sideEl = side;
			this.sideCreate = side.querySelector('.selekt-side-create');
			this.sideAdd = side.querySelector('.selekt-side-add');
			this.sideCreateHint = side.querySelector('.selekt-side-create-hint');
			this.sideTitle = side.querySelector('.selekt-side-title');
			this.sideText = side.querySelector('.selekt-side-text');
			this.sideInfoHint = side.querySelector('.selekt-side-info-hint');
			this.sideAdd.addEventListener('click', () => this.createItem());
		}

		this.listEl.addEventListener('scroll', () => {
			if (!this.more || this.loading) return;
			if (this.listEl.scrollTop + this.listEl.clientHeight >= this.listEl.scrollHeight - 40) this.loadMore();
		});
		this.listEl.addEventListener('click', (e) => {
			const row = e.target.closest('[data-index]');
			if (row) this.activate(parseInt(row.getAttribute('data-index'), 10), true);
			const createRow = e.target.closest('.selekt-create');
			if (createRow) this.createItem();
		});
		// Hovering moves the active row (lightweight - no full re-render), so
		// the description pane follows the mouse too, not just the keyboard.
		this.listEl.addEventListener('mouseover', (e) => {
			const row = e.target.closest('[data-index]');
			if (!row) return;
			const index = parseInt(row.getAttribute('data-index'), 10);
			if (index === this.activeIndex) return;
			const entry = this.loadedFlat[index];
			if (!entry || entry.item.disabled) return;
			this.listEl.querySelectorAll('.is-active').forEach((r) => r.classList.remove('is-active'));
			row.classList.add('is-active');
			this.activeIndex = index;
			this.renderSide();
		});

		return body;
	}

	// Modal mode: closed looks the same, open is a centered modal (like the
	// search modal) with its own search in the header. Mounted on <body> so
	// the backdrop stacks correctly; removed on destroy().
	buildModal() {
		const modal = document.createElement('div');
		modal.className = 'modal fade selekt-modal';
		modal.tabIndex = -1;
		modal.innerHTML = `<div class="modal-dialog modal-dialog-centered"><div class="modal-content"><div class="selekt-panel-search selekt-modal-head"><i class="ri-search-line" aria-hidden="true"></i><input type="text" class="selekt-search-input" placeholder="${t('Search...')}" autocomplete="off" aria-label="${t('Search options')}" /><button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="${t('Close')}"></button></div><div class="modal-body"></div></div></div>`;
		document.body.appendChild(modal);
		this.modalEl = modal;
		this.panelSearch = modal.querySelector('.selekt-search-input');
		modal.querySelector('.modal-body').appendChild(this.buildListBody());

		this.panelSearch.addEventListener('input', () => this.onSearch(this.panelSearch.value));
		this.panelSearch.addEventListener('keydown', (e) => this.onKeydown(e));
		modal.addEventListener('shown.bs.modal', () => this.panelSearch.focus());
		modal.addEventListener('hidden.bs.modal', () => {
			this.open = false;
			this.wrap.classList.remove('is-open');
			this.toggle.setAttribute('aria-expanded', 'false');
			if (this.multiple) this.inline.value = '';
			this.emit('selekt:close');
		});
	}

	buildSwitch() {
		const group = document.createElement('div');
		group.className = 'selekt-switch btn-group';
		group.setAttribute('role', 'group');
		const small = this.el.classList.contains('form-select-sm');
		this.source.load('', 1).then(({ items: loaded }) => {
			const flat = flatten(loaded).map((f) => f.item);
			flat.forEach((item) => {
				const btn = document.createElement('button');
				btn.type = 'button';
				btn.className = `btn btn-secondary${small ? ' btn-sm' : ''}`;
				btn.textContent = item.label;
				btn.disabled = item.disabled || this.el.disabled;
				btn.setAttribute('data-value', item.value);
				if (this.selection.has(String(item.value))) btn.classList.add('active');
				btn.addEventListener('click', () => {
					this.selection.clear();
					this.selection.set(String(item.value), { item, via: null });
					group.querySelectorAll('.btn').forEach((b) => b.classList.toggle('active', b === btn));
					this.syncHolder(true);
				});
				group.appendChild(btn);
			});
		});
		this.wrap.appendChild(group);
		this.switchEl = group;
	}

	// ---- open / close ----------------------------------------------------------

	async openPanel() {
		if (this.open || this.el.disabled || this.opts.mode === 'switch') return;
		this.open = true;
		this.wrap.classList.add('is-open');
		this.toggle.setAttribute('aria-expanded', 'true');
		this.emit('selekt:open');

		if (this.opts.mode === 'modal') {
			this.bsModal = window.bootstrap.Modal.getOrCreateInstance(this.modalEl);
			this.panelSearch.value = this.search = '';
			this.bsModal.show();
			await this.load(true);
			return;
		}

		this.panel.hidden = false;
		// Complex panels are wider than the control - flip to right-alignment
		// when the default left-alignment would run off the viewport.
		if (this.opts.mode === 'complex') {
			const rect = this.wrap.getBoundingClientRect();
			const panelWidth = this.panel.offsetWidth;
			const overflowsRight = rect.left + panelWidth > window.innerWidth - 16;
			const fitsFromRight = rect.right - panelWidth >= 16;
			this.panel.classList.toggle('selekt-panel-right', overflowsRight && fitsFromRight);
		}
		document.addEventListener('mousedown', this.onDocClick);

		if (this.panelSearch) {
			this.panelSearch.value = this.search = '';
			// Non-local and complex always search; local simple waits for the
			// first load to know whether it passes the threshold.
			let show = !this.source.local || this.opts.mode === 'complex';
			if (!show && this.lastLocalCount !== undefined) show = this.lastLocalCount >= this.opts.searchThreshold;
			this.searchBox.hidden = !show;
			if (show) setTimeout(() => this.panelSearch.focus(), 0);
		}
		await this.load(true);
		if (this.panelSearch && this.source.local && this.opts.mode !== 'complex') {
			const show = this.lastLocalCount >= this.opts.searchThreshold;
			if (show === this.searchBox.hidden) {
				this.searchBox.hidden = !show;
				if (show) setTimeout(() => this.panelSearch.focus(), 0);
			}
		}
	}

	closePanel() {
		if (!this.open) return;
		if (this.opts.mode === 'modal') {
			if (this.bsModal) this.bsModal.hide(); // state resets on hidden.bs.modal
			return;
		}
		this.open = false;
		this.wrap.classList.remove('is-open');
		this.toggle.setAttribute('aria-expanded', 'false');
		this.panel.hidden = true;
		document.removeEventListener('mousedown', this.onDocClick);
		if (this.multiple) { this.inline.value = ''; this.search = ''; }
		this.emit('selekt:close');
	}

	// ---- data ------------------------------------------------------------------

	onSearch(text) {
		this.closeOnEnter = false;
		this.search = text;
		clearTimeout(this.searchTimer);
		const delay = this.source.local ? 0 : this.opts.searchDebounce;
		this.searchTimer = setTimeout(() => this.load(true), delay);
	}

	async load(reset) {
		const token = ++this.seq;
		if (reset) { this.page = 1; this.loadedFlat = []; this.activeIndex = -1; }
		this.loading = true;
		if (reset) this.renderState('loading');
		try {
			const { items, more } = await this.source.load(this.search.trim(), this.page);
			if (token !== this.seq) return; // stale response - a newer request won
			this.more = more;
			if (this.source.local) this.lastLocalCount = this.search ? this.lastLocalCount : flatten(items).length;
			const flat = flatten(items);
			this.loadedFlat = reset ? flat : this.loadedFlat.concat(flat);
			this.renderList();
		} catch (err) {
			if (token !== this.seq) return;
			this.renderState('error', err.message);
		} finally {
			if (token === this.seq) this.loading = false;
		}
	}

	loadMore() {
		this.page += 1;
		this.load(false);
	}

	// ---- rendering ---------------------------------------------------------------

	renderState(state, message) {
		if (state === 'loading') this.listEl.innerHTML = `<div class="selekt-msg">${t('Loading...')}</div>`;
		if (state === 'error') this.listEl.innerHTML = `<div class="selekt-msg selekt-msg-error">${escapeHtml(message || 'Error')}</div>`;
	}

	renderList() {
		const rows = [];
		this.loadedFlat.forEach(({ item, depth }, index) => {
			const value = String(item.value);
			const selected = this.selection.has(value);
			const locked = selected && this.selection.get(value).via !== null;
			const classes = ['selekt-item'];
			if (item.disabled) classes.push('is-disabled');
			if (item.disabled && item.children) classes.push('is-group');
			if (selected) classes.push('is-selected');
			if (locked) classes.push('is-locked');
			if (index === this.activeIndex) classes.push('is-active');
			const icon = item.extra.icon ? `<i class="${escapeHtml(item.extra.icon)} selekt-item-icon" aria-hidden="true"></i>` : '';
			rows.push(`<div class="${classes.join(' ')}" role="option" id="${this.uid}-opt-${index}" aria-selected="${selected}" aria-disabled="${item.disabled || locked}" data-index="${index}" style="--selekt-depth:${depth}">${icon}<span class="selekt-item-label">${escapeHtml(item.label)}</span><i class="ri-check-line selekt-item-check" aria-hidden="true"></i></div>`);
		});

		if (!rows.length && !this.canCreate()) rows.push(`<div class="selekt-msg">${t('No results')}</div>`);
		// In simple mode the Add action is a row in the list; complex/modal
		// show it in the side pane instead.
		if (this.canCreate() && !this.sideEl) {
			rows.push(`<div class="selekt-create" role="option" id="${this.uid}-opt-create"><i class="ri-add-line" aria-hidden="true"></i> ${t('Add')} «${escapeHtml(this.search.trim())}»</div>`);
		}
		if (this.more) rows.push(`<div class="selekt-msg selekt-more">${t('Loading more on scroll...')}</div>`);
		this.listEl.innerHTML = rows.join('');
		this.renderSide();
	}

	// Side pane (complex/modal): the Add action on top + the active item's
	// title/description below.
	renderSide() {
		if (!this.sideEl) return;
		const createOn = !!this.opts.create;
		this.sideCreate.hidden = !createOn;
		if (createOn) {
			const can = this.canCreate();
			this.sideAdd.hidden = !can;
			if (can) this.sideAdd.textContent = `${t('Add')} «${this.search.trim()}»`;
			this.sideCreateHint.hidden = can;
		}
		const active = this.loadedFlat[this.activeIndex];
		if (active) {
			this.sideTitle.textContent = active.item.label;
			this.sideText.textContent = active.item.extra.description || '';
			this.sideTitle.hidden = false;
			this.sideText.hidden = !active.item.extra.description;
			this.sideInfoHint.hidden = true;
		} else {
			this.sideTitle.hidden = true;
			this.sideText.hidden = true;
			this.sideInfoHint.hidden = false;
		}
	}

	renderClosed() {
		const visible = Array.from(this.selection.values()).filter((s) => s.via === null);
		this.wrap.classList.toggle('has-value', this.selection.size > 0);
		this.clearBtn.hidden = !(this.opts.clearable && this.selection.size && !this.el.disabled);
		this.wrap.classList.toggle('has-clear', !this.clearBtn.hidden);

		if (!this.multiple) {
			const first = visible[0];
			this.valueEl.textContent = first ? first.item.label : (this.opts.placeholder ? t(this.opts.placeholder) : t('Select...'));
			this.valueEl.classList.toggle('is-placeholder', !first);
			return;
		}

		// maxTagsVisible: false = all tags, 0 = no tags (just "N items
		// selected"), N > 0 = first N tags + "+x more".
		const max = this.opts.maxTagsVisible;
		let shown = visible;
		if (max === 0) shown = [];
		else if (typeof max === 'number' && max > 0) shown = visible.slice(0, max);
		const rest = max === 0 ? 0 : visible.length - shown.length;
		// Rebuild the tags but leave the inline input in place (it is the last
		// child of tagsEl; clearing it too would blur it mid-typing).
		Array.from(this.tagsEl.children).forEach((child) => { if (child !== this.inline) child.remove(); });
		const put = (node) => this.tagsEl.insertBefore(node, this.inline);
		if (max === 0 && visible.length) {
			const count = document.createElement('span');
			count.className = 'selekt-count';
			count.textContent = `${visible.length} ${visible.length === 1 ? t('item selected') : t('items selected')}`;
			put(count);
		}
		shown.forEach(({ item }) => {
			const tag = document.createElement('span');
			tag.className = 'selekt-tag' + (item.children && this.opts.selectChildren ? ' selekt-tag-children' : '');
			tag.innerHTML = `<span>${escapeHtml(item.label)}</span>`;
			const x = document.createElement('button');
			x.type = 'button';
			x.className = 'selekt-tag-x';
			x.setAttribute('aria-label', `${t('Remove')} ${item.label}`);
			x.innerHTML = '<i class="ri-close-line" aria-hidden="true"></i>';
			x.addEventListener('click', (e) => { e.stopPropagation(); this.deselect(item.value); });
			tag.appendChild(x);
			put(tag);
		});
		if (rest > 0) {
			const moreTag = document.createElement('span');
			moreTag.className = 'selekt-tag selekt-tag-more';
			moreTag.textContent = `+${rest} ${t('more')}`;
			put(moreTag);
		}
		this.inline.placeholder = visible.length ? '' : (this.opts.placeholder ? t(this.opts.placeholder) : t('Select...'));
	}

	// ---- selection ----------------------------------------------------------------

	activate(index, chose) {
		const entry = this.loadedFlat[index];
		if (!entry) return;
		if (entry.item.disabled) return;
		this.activeIndex = index;
		if (chose) this.choose(entry.item);
		else { this.renderList(); this.scrollActiveIntoView(); }
	}

	choose(item) {
		const value = String(item.value);
		const existing = this.selection.get(value);
		if (existing && existing.via !== null) return; // locked by a selected parent

		if (!this.multiple) {
			this.selection.clear();
			this.selection.set(value, { item, via: null });
			this.syncHolder(true);
			this.closePanel();
			this.toggle.focus();
			return;
		}

		if (existing) {
			this.deselect(value);
			return;
		}
		this.selection.set(value, { item, via: null });
		if (this.opts.selectChildren && item.children) {
			// Absorb ALL selectable descendants under the parent - including any
			// selected explicitly beforehand - so the closed control shows one
			// parent tag, and deselecting the parent cleans the whole subtree.
			selectableDescendants(item).forEach((child) => {
				this.selection.set(String(child.value), { item: child, via: value });
			});
		}
		this.inline.value = '';
		if (this.search) { this.search = ''; this.load(true); }
		this.syncHolder(true);
		this.renderList();
		this.inline.focus();
	}

	deselect(value) {
		value = String(value);
		const entry = this.selection.get(value);
		if (!entry || entry.via !== null) return; // locked children can't be removed directly
		this.selection.delete(value);
		// drop everything auto-selected via this parent
		Array.from(this.selection.entries()).forEach(([v, s]) => {
			if (s.via === value) this.selection.delete(v);
		});
		this.syncHolder(true);
		if (this.open) this.renderList();
	}

	clear() {
		if (!this.selection.size) return;
		this.selection.clear();
		this.syncHolder(true);
		if (this.open) this.renderList();
	}

	canCreate() {
		if (!this.opts.create || !this.search.trim()) return false;
		const needle = this.search.trim().toLowerCase();
		return !this.loadedFlat.some(({ item }) => item.label.toLowerCase() === needle);
	}

	async createItem() {
		const label = this.search.trim();
		// one create at a time (the race-conditions scan): a double-click on
		// the «Add "x"» row posted the endpoint twice before either answered
		// — two items minted server-side; the flag is set synchronously
		if (!label || this.creating) return;
		this.creating = true;
		try {
			let item;
			if (this.opts.create === true) {
				item = normalizeItems([{ value: label, label, extra: { created: true } }])[0];
				this.source.add(item);
			} else {
				// create via endpoint: send label + the same business params
				const res = await window.g.fetch(String(this.opts.create), { label, ...this.collectParams() });
				if (!res.success) { window.g.error(res.error.message); return; }
				if (res.error && res.error.code) { this.renderState('error', res.error.message); return; }
				this.source.clearCache();
				item = normalizeItems([res.data])[0];
			}

			// Clear the search BEFORE selecting, so choose() doesn't schedule its
			// own (racing) reload for the old search text.
			this.search = '';
			if (this.panelSearch) this.panelSearch.value = '';
			this.choose(item);
			if (!this.multiple) return; // choose() already closed the panel/modal

			// Reload the full list and reveal the freshly added item.
			await this.load(true);
			const index = this.loadedFlat.findIndex((f) => String(f.item.value) === String(item.value));
			if (index >= 0) {
				this.activeIndex = index;
				this.renderList();
				this.scrollActiveIntoView();
			}
			// Modal: one more Enter closes it.
			if (this.opts.mode === 'modal') {
				this.closeOnEnter = true;
				this.panelSearch.focus();
			}
		} finally {
			this.creating = false;
		}
	}

	// ---- holder sync + events --------------------------------------------------------

	values() {
		return Array.from(this.selection.keys());
	}

	syncHolder(fireChange) {
		const el = this.el;
		el.innerHTML = '';
		this.selection.forEach(({ item }, value) => {
			const o = document.createElement('option');
			o.value = value;
			o.textContent = item.label;
			o.selected = true;
			el.appendChild(o);
		});
		// Single with nothing selected: keep an empty option so the field still
		// submits its key (value "") instead of disappearing from the form.
		if (!this.multiple && !this.selection.size) {
			const o = document.createElement('option');
			o.value = '';
			o.selected = true;
			el.appendChild(o);
		}
		if (this.opts.mode !== 'switch') this.renderClosed();
		// Fire only when the value ACTUALLY changed (re-picking the same item
		// must not ripple into listeners / dependent selects).
		const signature = this.values().join('');
		if (fireChange && signature !== this.lastFired) {
			this.lastFired = signature;
			el.dispatchEvent(new Event('change', { bubbles: true }));
			this.emit('selekt:change', { values: this.values() });
		}
	}

	emit(name, detail) {
		this.el.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { selekt: this, ...detail } }));
	}

	// ---- keyboard --------------------------------------------------------------------

	onKeydown(e) {
		if (e.key !== 'Enter') this.closeOnEnter = false;
		if (e.key === 'Backspace' && this.multiple && this.inline.value === '') {
			const visible = Array.from(this.selection.values()).filter((s) => s.via === null);
			const last = visible[visible.length - 1];
			if (last) { this.deselect(last.item.value); return; }
		}
		if (!this.open && ['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key) && e.target === this.toggle) {
			e.preventDefault();
			this.openPanel();
			return;
		}
		if (!this.open) return;
		if (e.key === 'Escape') { this.closePanel(); this.toggle.focus(); return; }
		if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
			e.preventDefault();
			const dir = e.key === 'ArrowDown' ? 1 : -1;
			let i = this.activeIndex;
			for (let step = 0; step < this.loadedFlat.length; step++) {
				i += dir;
				if (i < 0 || i >= this.loadedFlat.length) break;
				if (!this.loadedFlat[i].item.disabled) { this.activate(i, false); break; }
			}
			return;
		}
		if (e.key === 'Enter') {
			e.preventDefault();
			if (this.closeOnEnter) {
				this.closeOnEnter = false;
				this.closePanel();
				return;
			}
			if (this.activeIndex >= 0 && this.loadedFlat[this.activeIndex]) this.activate(this.activeIndex, true);
			else if (this.canCreate()) this.createItem();
		}
	}

	scrollActiveIntoView() {
		const row = this.listEl.querySelector('.is-active');
		if (row) row.scrollIntoView({ block: 'nearest' });
	}

	// ---- dependsOn ----------------------------------------------------------------------

	bindDepends() {
		const dep = this.opts.dependsOn;
		if (!dep || !dep.source || !dep.param) return;
		const parent = document.querySelector(dep.source);
		if (!parent) return;
		this.updateDependState(parent);
		this.onParentChange = async () => {
			// same generation counter as setValue: a stale lookup resolving
			// after a newer selection write must not merge into it
			const mine = this.valueSeq = (this.valueSeq || 0) + 1;
			this.source.clearCache();
			const values = this.values();
			if (this.opts.keepValue && values.length && parent.value) {
				const found = await this.source.lookup(values.map(coerce));
				if (mine !== this.valueSeq) return; // a newer write owns the selection
				const keep = new Set(found.map((f) => String(f.value)));
				let changed = false;
				values.forEach((v) => { if (!keep.has(v)) { this.selection.delete(v); changed = true; } });
				if (changed || this.selection.size !== values.length) this.syncHolder(true);
				else this.renderClosed();
			} else if (values.length) {
				this.selection.clear();
				this.syncHolder(true);
			}
			this.updateDependState(parent);
			if (this.open) this.load(true);
		};
		parent.addEventListener('change', this.onParentChange);
		this.dependParent = parent;
	}

	updateDependState(parent) {
		const enabled = !!parent.value && !this.el.disabled;
		if (this.toggle) this.toggle.disabled = !enabled;
		this.wrap.classList.toggle('is-waiting-parent', !enabled);
	}

	// ---- misc public API -------------------------------------------------------------------

	getValue() {
		const v = this.values().map(coerce);
		return this.multiple ? v : (v[0] !== undefined ? v[0] : null);
	}

	// Accepts a scalar, an array, or {value,label} object(s). Unknown labels
	// are resolved through source.lookup (the data.ids convention).
	async setValue(value) {
		// generation counter (the race-conditions scan): a setValue whose
		// network lookup resolves AFTER a newer setValue/parent-change used
		// to add its items ON TOP of the fresh selection — the holder ended
		// as the union and the late change event fired after the owner's
		// guards dropped (phantom filter values). Stale continuations are
		// discarded whole.
		const mine = this.valueSeq = (this.valueSeq || 0) + 1;
		const list = (Array.isArray(value) ? value : [value]).filter((v) => v !== null && v !== undefined && v !== '');
		this.selection.clear();
		const toResolve = [];
		list.forEach((v) => {
			if (typeof v === 'object') this.selection.set(String(v.value), { item: normalizeItems([v])[0], via: null });
			else toResolve.push(v);
		});
		if (toResolve.length) {
			const found = await this.source.lookup(toResolve);
			if (mine !== this.valueSeq) return; // a newer write owns the selection
			found.forEach((item) => this.selection.set(String(item.value), { item, via: null }));
			toResolve.forEach((v) => {
				if (!this.selection.has(String(v))) this.selection.set(String(v), { item: { value: v, label: String(v), extra: {}, children: null }, via: null });
			});
		}
		this.syncHolder(true);
	}

	refresh() {
		this.source.clearCache();
		if (this.open) this.load(true);
	}

	bindFormReset() {
		const form = this.el.closest('form');
		if (!form) return;
		this.onFormReset = () => setTimeout(() => this.setValue(this.initialValues), 0);
		form.addEventListener('reset', this.onFormReset);
	}

	destroy() {
		document.removeEventListener('mousedown', this.onDocClick || (() => {}));
		if (this.bsModal) this.bsModal.dispose();
		if (this.modalEl) this.modalEl.remove();
		if (this.dependParent) this.dependParent.removeEventListener('change', this.onParentChange);
		const form = this.el.closest('form');
		if (form && this.onFormReset) form.removeEventListener('reset', this.onFormReset);
		this.wrap.parentNode.insertBefore(this.el, this.wrap);
		this.wrap.remove();
		this.el.classList.remove('selekt-holder');
		this.el.removeAttribute('aria-hidden');
		this.el.tabIndex = 0;
		delete this.el.selekt;
	}
}

function escapeHtml(s) {
	return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}

export function coerce(v) {
	if (typeof v !== 'string') return v;
	if (v === 'true') return true;
	if (v === 'false') return false;
	if (v !== '' && !isNaN(v) && String(Number(v)) === v) return Number(v);
	return v;
}
